import React from "react"
import {CheckCircle2, Info, Scale, Zap, Shield, Truck} from 'lucide-react'

const BuyingGuide = () => {
  const comparisonFeatures = [
    {
      category: "Capacity",
      basic: "Up to 12' x 16' sheds",
      standard: "Up to 14' x 40' sheds",
      premium: "Up to 16' x 56' sheds"
    },
    {
      category: "Weight Capacity",
      basic: "Up to 6,000 lbs",
      standard: "Up to 12,000 lbs",
      premium: "Up to 20,000 lbs"
    },
    {
      category: "Terrain Capability",
      basic: "Flat, paved surfaces",
      standard: "Moderate slopes, gravel",
      premium: "Rough terrain, steep grades"
    },
    {
      category: "Power Type",
      basic: "Manual hydraulic",
      standard: "Electric hydraulic",
      premium: "Dual hydraulic system"
    }
  ]

  const keyFactors = [
    {
      icon: <Scale className="text-orange-500" size={32} />,
      title: "Load Capacity Requirements",
      description: "Calculate the maximum weight of structures you'll transport, including building materials and contents. Always choose equipment rated 20-30% above your typical loads for safety margins."
    },
    {
      icon: <Truck className="text-orange-500" size={32} />,
      title: "Terrain Conditions",
      description: "Assess the typical ground conditions at pickup and delivery sites. Soft soil, slopes, and uneven terrain require more powerful hydraulic systems and specialized tire configurations."
    },
    {
      icon: <Zap className="text-orange-500" size={32} />,
      title: "Frequency of Use",
      description: "Daily commercial operations demand industrial-grade equipment with higher duty cycles. Occasional users may find mid-range models more cost-effective for their needs."
    },
    {
      icon: <Shield className="text-orange-500" size={32} />,
      title: "Maintenance & Support",
      description: "Consider local parts availability, warranty coverage, and service network proximity. Downtime costs can exceed the initial equipment investment over time."
    }
  ]

  return (
    <div className="min-h-screen bg-zinc-900 text-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 py-20 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-6">
              <span className="text-orange-500 font-semibold text-sm">EDUCATIONAL RESOURCE</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Shed Moving Equipment <span className="text-orange-500">Buying Guide</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Comprehensive guide to selecting the right mule shed equipment for your operation. Learn about specifications, features, and key decision factors.
            </p>
          </div>
        </div>
      </section>

      {/* Key Factors Section */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Key Selection Factors</h2>
            <p className="text-xl text-gray-400">Critical considerations when evaluating shed moving equipment</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {keyFactors.map((factor, index) => (
              <div key={index} className="bg-zinc-800 border border-zinc-700 rounded-xl p-8 hover:border-orange-500/50 transition-all">
                <div className="mb-4">{factor.icon}</div>
                <h3 className="text-2xl font-bold mb-4">{factor.title}</h3>
                <p className="text-gray-300 leading-relaxed">{factor.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Comparison Table */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Equipment Class Comparison</h2>
            <p className="text-xl text-gray-400">Understanding different equipment tiers and capabilities</p>
          </div>

          <div className="bg-zinc-800 border border-zinc-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-900">
                  <tr>
                    <th className="px-6 py-4 text-left text-orange-500 font-bold text-lg">Feature</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-bold text-lg">Basic Class</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-bold text-lg">Standard Class</th>
                    <th className="px-6 py-4 text-left text-gray-300 font-bold text-lg">Premium Class</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, index) => (
                    <tr key={index} className="border-t border-zinc-700 hover:bg-zinc-900/50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-orange-500">{feature.category}</td>
                      <td className="px-6 py-4 text-gray-300">{feature.basic}</td>
                      <td className="px-6 py-4 text-gray-300">{feature.standard}</td>
                      <td className="px-6 py-4 text-gray-300">{feature.premium}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Decision Framework */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Decision Framework</h2>
            <p className="text-xl text-gray-400">Step-by-step evaluation process for equipment selection</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 rounded-xl p-8">
              <div className="text-5xl font-bold text-orange-500 mb-4">01</div>
              <h3 className="text-2xl font-bold mb-4">Assess Your Needs</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Average structure size</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Monthly move volume</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Typical site conditions</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Distance traveled per move</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 rounded-xl p-8">
              <div className="text-5xl font-bold text-orange-500 mb-4">02</div>
              <h3 className="text-2xl font-bold mb-4">Calculate ROI</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Equipment cost vs. rental</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Maintenance expenses</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Fuel and operating costs</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Productivity gains</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 rounded-xl p-8">
              <div className="text-5xl font-bold text-orange-500 mb-4">03</div>
              <h3 className="text-2xl font-bold mb-4">Evaluate Options</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">New vs. used equipment</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Warranty coverage</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Parts availability</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-300">Service network access</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications Guide */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Understanding Technical Specifications</h2>
            <p className="text-xl text-gray-400">Key specifications explained for informed decision-making</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 hover:border-orange-500/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <Info className="text-orange-500" size={24} />
                <h3 className="text-xl font-bold">Load Rating</h3>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Maximum weight the equipment can safely lift and transport. Always factor in a 20-30% safety margin above your typical loads.
              </p>
            </div>

            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 hover:border-orange-500/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <Info className="text-orange-500" size={24} />
                <h3 className="text-xl font-bold">Hydraulic System</h3>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Determines lifting power and precision. Electric systems offer faster cycle times, while manual systems provide better control on delicate lifts.
              </p>
            </div>

            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 hover:border-orange-500/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <Info className="text-orange-500" size={24} />
                <h3 className="text-xl font-bold">Clearance Height</h3>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Minimum ground clearance required for positioning equipment under structures. Critical for sites with low overhangs or tight spaces.
              </p>
            </div>

            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 hover:border-orange-500/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <Info className="text-orange-500" size={24} />
                <h3 className="text-xl font-bold">Tire Configuration</h3>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Impacts mobility on different terrain types. Wide footprint tires distribute weight better on soft ground, while harder compounds suit paved surfaces.
              </p>
            </div>

            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 hover:border-orange-500/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <Info className="text-orange-500" size={24} />
                <h3 className="text-xl font-bold">Duty Cycle</h3>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Frequency of continuous operation the equipment can sustain. Commercial operations require industrial-duty ratings for daily use.
              </p>
            </div>

            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-6 hover:border-orange-500/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <Info className="text-orange-500" size={24} />
                <h3 className="text-xl font-bold">Maneuverability</h3>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Turning radius and navigation capability in tight spaces. Essential for residential sites with limited access or narrow driveways.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* New vs Used Consideration */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">New vs. Used Equipment</h2>
            <p className="text-xl text-gray-400">Weighing the advantages of different purchasing options</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-orange-500/10 to-zinc-900 border-2 border-orange-500/30 rounded-xl p-8">
              <h3 className="text-3xl font-bold mb-6 text-orange-500">New Equipment</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold text-white mb-1">Full Warranty Coverage</p>
                    <p className="text-gray-300 text-sm">Comprehensive protection against defects and failures</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold text-white mb-1">Latest Technology</p>
                    <p className="text-gray-300 text-sm">Modern features, improved efficiency, better safety systems</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold text-white mb-1">No Hidden Issues</p>
                    <p className="text-gray-300 text-sm">Zero operational history, pristine condition</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold text-white mb-1">Financing Options</p>
                    <p className="text-gray-300 text-sm">Easier to secure favorable loan terms and rates</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 rounded-xl p-8">
              <h3 className="text-3xl font-bold mb-6">Used Equipment</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-gray-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold text-white mb-1">Lower Initial Cost</p>
                    <p className="text-gray-300 text-sm">Significant savings on purchase price, 30-60% less than new</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-gray-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold text-white mb-1">Proven Performance</p>
                    <p className="text-gray-300 text-sm">Known reliability record, established maintenance history</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-gray-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold text-white mb-1">Immediate Availability</p>
                    <p className="text-gray-300 text-sm">No manufacturing wait times, ready to deploy</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="text-gray-400 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-semibold text-white mb-1">Lower Depreciation</p>
                    <p className="text-gray-300 text-sm">Slower value loss, better resale retention</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default BuyingGuide
